import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import logoImg from '../assets/logo.png';
import Translate from './Translate';

const Footer = () => {
  const socials = [
    {
      icon: ({ size }) => (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
        </svg>
      ),
      url: 'https://instagram.com/',
      hoverColor: 'hover:bg-pink-600 hover:shadow-pink-600/20'
    },
    {
      icon: ({ size }) => (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
        </svg>
      ),
      url: 'https://facebook.com/',
      hoverColor: 'hover:bg-blue-600 hover:shadow-blue-600/20'
    },
    {
      icon: ({ size }) => (
        <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
      url: 'https://wa.me/919890016548',
      hoverColor: 'hover:bg-emerald hover:shadow-emerald/20'
    }
  ];

  return (
    <footer className="bg-[#121212] text-white pt-20 pb-10 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Top Branding & Social Row */}
        <div className="flex flex-col md:flex-row justify-between items-center md:items-center text-center md:text-left pb-12 border-b border-white/10 gap-6">
          <div className="max-w-md flex flex-col items-center md:items-start text-center md:text-left">
            <Link to="/" className="inline-block mb-4">
              <img
                src={logoImg}
                alt="Sangram Sugarcane Logo"
                className="h-16 w-auto object-contain brightness-110"
              />
            </Link>
            <p className="text-gray-400 font-opensans text-sm leading-relaxed">
              <Translate>Delivering premium jaggery, jaggery powder, and organic fertilizer through modern technology, natural quality, and a farmer-centric approach.</Translate>
            </p>
          </div>
          <div className="flex space-x-4">
            {socials.map((social, idx) => (
              <a
                key={idx}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-gray-300 ${social.hoverColor} hover:text-white transition-all duration-300 hover:shadow-lg`}
              >
                <social.icon size={20} />
              </a>
            ))}
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 pt-16 pb-16 text-center md:text-left">

          {/* Registered Office */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-xs font-montserrat font-bold uppercase tracking-widest text-sugarcane-light mb-8 relative inline-block">
              <Translate>Registered Office</Translate>
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 w-8 h-0.5 bg-sugarcane-green"></span>
            </h3>
            <div className="space-y-4 font-opensans flex flex-col items-center md:items-start">
              <h4 className="text-base font-bold text-white mb-2"><Translate>Sangram Bhawan</Translate></h4>
              <div className="flex items-start justify-center md:justify-start space-x-3 text-gray-400 text-sm leading-relaxed text-center md:text-left">
                <MapPin className="text-sugarcane-green flex-shrink-0 mt-0.5" size={16} />
                <span>
                  <Translate>Plot No. 187/1, Tasgaon-Karad Road,</Translate><br />
                  <Translate>Industrial Estate, Palus - 416310</Translate><br />
                  <Translate>Tal. Palus, Dist. Sangli</Translate>
                </span>
              </div>
              <div className="flex items-start justify-center md:justify-start space-x-3 text-gray-400 text-sm pt-2 text-center md:text-left">
                <Phone className="text-sugarcane-green flex-shrink-0 mt-0.5" size={16} />
                <div className="space-y-1 font-semibold">
                  <a href="tel:02346226240" className="hover:text-sugarcane-green transition-colors block">(02346) 226240</a>
                  <a href="tel:02346229200" className="hover:text-sugarcane-green transition-colors block">(02346) 229200</a>
                  <a href="tel:+919890016548" className="hover:text-sugarcane-green transition-colors block">+91 98900 16548</a>
                </div>
              </div>
            </div>
          </div>

          {/* Factory & Works */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-xs font-montserrat font-bold uppercase tracking-widest text-sugarcane-light mb-8 relative inline-block">
              <Translate>Factory & Works</Translate>
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 w-8 h-0.5 bg-sugarcane-green"></span>
            </h3>
            <div className="space-y-4 font-opensans flex flex-col items-center md:items-start">
              <h4 className="text-base font-bold text-white mb-2"><Translate>Sangram Cane Agro Limited</Translate></h4>
              <div className="flex items-start justify-center md:justify-start space-x-3 text-gray-400 text-sm leading-relaxed text-center md:text-left">
                <MapPin className="text-sugarcane-green flex-shrink-0 mt-0.5" size={16} />
                <span>
                  <Translate>Bhilwadi-Sukhwadi Road,</Translate><br />
                  <Translate>Bhilwadi - 416303</Translate><br />
                  <Translate>Tal. Palus, Dist. Sangli</Translate>
                </span>
              </div>
              <div className="flex items-center justify-center md:justify-start space-x-3 text-gray-400 text-sm pt-2 text-center md:text-left">
                <Mail className="text-sugarcane-green flex-shrink-0" size={16} />
                <a href="mailto:sangramcaneagroltd@gmail.com" className="hover:text-sugarcane-green transition-colors break-all">sangramcaneagroltd@gmail.com</a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-center md:items-start">
            <h3 className="text-xs font-montserrat font-bold uppercase tracking-widest text-sugarcane-light mb-8 relative inline-block">
              <Translate>Quick Links</Translate>
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 w-8 h-0.5 bg-sugarcane-green"></span>
            </h3>
            <ul className="space-y-3 flex flex-col items-center md:items-start">
              {['Home', 'About Us', 'Products', 'Our Strengths', 'Careers'].map((item) => (
                <li key={item}>
                  <Link
                    to={`/${item.toLowerCase().replace(' ', '-')}`}
                    className="text-gray-400 hover:text-sugarcane-green transition-all duration-300 flex items-center group text-sm"
                  >
                    <span className="w-1.5 h-1.5 bg-sugarcane-green rounded-full mr-2 opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"></span>
                    <Translate>{item}</Translate>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-center md:text-left text-gray-500 text-sm">
          <p><Translate>© 2026 Sangram Cane Agro Limited. All Rights Reserved.</Translate></p>
          <div className="mt-4 md:mt-0 text-gray-500">
            <Translate>Designed & Developed by</Translate> <a href="https://sahyadritech.in/" target="_blank" rel="noopener noreferrer" className="font-bold text-gray-400 hover:text-sugarcane-green transition-colors">Sahyadri Tech</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
